import axios from 'axios';
import { firebaseAuth, auth } from '../firebase'; // Import auth directly

const API_URL = '/api';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor to add Auth Token to every request
api.interceptors.request.use(
    async (config) => {
        let token = localStorage.getItem('authToken');

        // Try to get a fresh token from Firebase Auth SDK if user is logged in
        if (auth.currentUser) {
            try {
                token = await auth.currentUser.getIdToken();
                // Optionally update localStorage, though not strictly necessary if we always ask SDK
                localStorage.setItem('authToken', token);
            } catch (error) {
                console.error("Error getting fresh token:", error);
            }
        }

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor to handle 401 errors and refresh token
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // Attempt to refresh token
                console.log("🔄 401 Detected! Refreshing token...");
                const newToken = await firebaseAuth.refreshToken();

                // Update header and retry original request
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                console.error("❌ Token refresh failed:", refreshError);
                firebaseAuth.signOut();
                // Redirect to login or show alert
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }
        return Promise.reject(error);
    }
);

export const authAPI = {
    syncUser: (role, full_name = null) => api.post('/auth/sync', { role, ...(full_name && { full_name }) }),
    // Note: Login/Register are handled by Firebase Client SDK usually, 
    // but if we are proxying through backend or just syncing, we keep syncUser.
    // However, the previous code used a proxy for login/signup. 
    // Since we are keeping Firebase Auth on client, we should use Firebase SDK directly in React,
    // OR if the previous proxy was doing it, we might need to replicate that or just use Firebase SDK.
    // BUT, looking at the previous Login.js, it was sending email/password to a proxy.
    // To make it simple and standard, we should probably use Firebase SDK in React.
    // BUT, to minimize changes, if the backend has auth routes (it doesn't seem to have login/signup, only sync),
    // checking backend authRoutes... it only has /sync.
    // So the user MUST use Firebase SDK on frontend to get the token, then call sync.
    // The previous proxy likely wrapped Firebase Admin SDK or REST API.

    // Let's assume we will switch to Firebase Client SDK in Login.js.
    // But for now, let's just export the APIs we have.
    updateProfile: (data) => api.put('/auth/me', { full_name: data.name, phone: data.phone, bio: data.bio, location: data.location }),
    getAllTeachers: () => api.get('/auth/teachers'),
    toggleUserStatus: (userId, status) => api.put(`/auth/users/${userId}/status`, { status }),
    deleteTeacher: (userId) => api.delete(`/auth/users/${userId}`),
};

export const coursesAPI = {
    getAll: () => api.get('/courses'),
    create: (data) => api.post('/courses', data),
    join: (accessCode) => api.post('/courses/join', { accessCode }),
    delete: (courseId) => api.delete(`/courses/${courseId}`),
    getEnrolledStudents: () => api.get('/courses/students'),
    getEnrollments: (courseId, status) => api.get(`/courses/${courseId}/enrollments`, { params: { status } }),
};

export const enrollmentAPI = {
    getEnrollments: (courseId, status) => api.get(`/courses/${courseId}/enrollments`, { params: { status } }),
    approve: (enrollmentId) => api.post(`/enrollments/${enrollmentId}/approve`),
    reject: (enrollmentId) => api.post(`/enrollments/${enrollmentId}/reject`),
    block: (enrollmentId) => api.post(`/enrollments/${enrollmentId}/block`),
    remove: (enrollmentId) => api.post(`/enrollments/${enrollmentId}/remove`),
};

export const examsAPI = {
    getByCourse: (courseId) => api.get(`/exams/course/${courseId}`),
    getDetails: (examId) => api.get(`/exams/${examId}`),
    create: (data) => api.post('/exams', data),
    addQuestions: (examId, questions) => api.post(`/exams/${examId}/questions`, { questions }),
    getById: (examId) => api.get(`/exams/${examId}`),
    update: (examId, data) => api.put(`/exams/${examId}`, data),
    deleteQuestion: (questionId) => api.delete(`/exams/question/${questionId}`),
    delete: (examId) => api.delete(`/exams/${examId}`),
    importWordPreview: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/exams/import/word/preview', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
};

export const uploadsAPI = {
    uploadLecture: (formData) => api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    getLectures: (courseId) => api.get(`/uploads/${courseId}`),
    getLecture: (id) => api.get(`/uploads/lecture/${id}`),
    updateLecture: (id, formData) => api.put(`/uploads/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    deleteLecture: (id) => api.delete(`/uploads/${id}`),
};

export const analyticsAPI = {
    getStats: () => api.get('/analytics/dashboard'),
    getOverview: () => api.get('/analytics/overview'),
};

export const studentsAPI = {
    getAll: () => api.get('/students/all'),
    delete: (studentId) => api.delete(`/students/${studentId}`),
};

export const progressAPI = {
    getStudentExams: (courseId, studentId) => api.get(`/progress/courses/${courseId}/students/${studentId}/exams`),
    getAttemptDetails: (attemptId) => api.get(`/progress/attempts/${attemptId}`),
    updateAnswerGrade: (attemptId, answerId, pointsEarned) => api.put(`/progress/attempts/${attemptId}/grade`, { answerId, points_earned: pointsEarned }),
    getPendingReviews: (courseId) => api.get(`/progress/courses/${courseId}/pending-reviews`),
};

export const bannersAPI = {
    getAll: () => api.get('/banners/all'),
    getActive: () => api.get('/banners'),
    create: (data) => api.post('/banners', data),
    update: (id, data) => api.put(`/banners/${id}`, data),
    delete: (id) => api.delete(`/banners/${id}`),
};

export const activationAPI = {
    // Teacher: onboarding checklist
    getChecklist: () => api.get('/teacher/activation-checklist'),

    // Admin: email rules
    getRules:   () => api.get('/admin/activation-email-rules'),
    updateRule: (id, data) => api.put(`/admin/activation-email-rules/${id}`, data),
    testRule:   (id, data) => api.post(`/admin/activation-email-rules/${id}/test`, data),
    getLogs:    (params) => api.get('/admin/activation-email-logs', { params }),
};

export const aiCreditsAPI = {
    // Teacher
    getMyCredits: () => api.get('/ai-credits/my'),
    getPackages: () => api.get('/ai-credits/packages'),
    submitRequest: (data) => api.post('/ai-credits/request', data),
    cancelRequest: (id) => api.put(`/ai-credits/request/${id}/cancel`),

    // Admin
    getAllRequests: (params) => api.get('/ai-credits/admin/requests', { params }),
    processRequest: (id, data) => api.put(`/ai-credits/admin/requests/${id}`, data),
    manualCredit: (data) => api.post('/ai-credits/admin/manual-credit', data),
    getStats: () => api.get('/ai-credits/admin/stats'),
    getTeachersBalance: () => api.get('/ai-credits/admin/teachers-balance'),
    getAdminPackages: () => api.get('/ai-credits/admin/packages'),
    createPackage: (data) => api.post('/ai-credits/admin/packages', data),
    updatePackage: (id, data) => api.put(`/ai-credits/admin/packages/${id}`, data),
};

export default api;

